import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import {
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  MoreVertIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import {
  scrollToSelectedAnchor,
  useRedirectUponFetchFailure,
} from "utils/common";
import { addQueryParams, reverse } from "utils/urls";

import Box from "components/Box";
import Button from "components/Button";
import ButtonDropdown from "components/ButtonDropdown";
import GroupLabel from "components/ButtonDropdown/GroupLabel";
import Collapse from "components/Collapse";
import { ID as SUB_NAVIGATION_ID } from "components/DealPageLayout/SubNavigation";
import { LinearProgress } from "components/Loading";
import { ID as MAIN_NAVIGATION_ID } from "components/MainNavigation";
import { MenuItem } from "components/Menu";
import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import ProgressBar from "components/ProgressBar";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Switch from "components/Switch";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { usePageTitle } from "components/usePageTitle";

import { useDueDiligenceFilters } from "pages/Deal/DueDiligence/Filters";

import { useSignedUser } from "entities/Auth/sdk";
import FilteredViews from "entities/Checklist/components/FilteredViews";
import Footer from "entities/Checklist/components/Footer";
import ItemSearch from "entities/Checklist/components/ItemSearch";
import Notifications from "entities/Checklist/components/Notifications";
import {
  useChecklistProgress,
  useNotificationCountForChecklist,
} from "entities/Checklist/components/Notifications/hooks";
import { useCompanySettings } from "entities/Company/sdk";
import AIOnboardingButtons from "entities/Deal/components/AIOnboardingButtons";
import OnboardingChecklistFieldCategories from "entities/Deal/components/AIOnboardingButtons/OnboardingChecklistFieldCategories";
import { useLoggedUserIsDealAdmin } from "entities/Deal/hooks";
import {
  useDealChecklistPermission,
  useDealDetails,
  useOnboardingCompanyPackageCategorizedFileFieldCounts,
} from "entities/Deal/sdk";
import { FieldType } from "entities/Field/sdk";
import ActionButton from "entities/Package/components/ActionButton";
import Category from "entities/Package/components/Category";
import CategorySkeleton from "entities/Package/components/CategorySkeleton";
import LeftMenu from "entities/Package/components/LeftMenu";
import SavedSnapshots from "entities/Package/components/SavedSnapshots";
import ShareSettings from "entities/Package/components/ShareSettings";
import ShareWithButton from "entities/Package/components/ShareWithButton";
import StatusSelector from "entities/Package/components/StatusSelector";
import DueDiligenceTabs from "entities/Package/components/Tabs";
import { DueDiligenceTabChoices } from "entities/Package/components/Tabs/constants";
import ZipDetails from "entities/Package/components/ZipDetails";
import {
  fieldMarkAsSeenBulk,
  useCompanyPackageSnapshotList,
  useCompanyPackageStatusCounts,
  useCompanyPackageWithTabsInformation,
  usePackageDealDetails,
} from "entities/Package/sdk";
import {
  useCompanyPackageSnapshotProcessing,
  useCompanySectionAutomaticFillProcessing,
  useZipProcessing,
} from "entities/TableStorage/hooks";
import DueDiligenceTabOpenTaskButton from "entities/Task/components/DueDiligenceTabOpenTaskButton";
import { useTagList, useTaskChannels } from "entities/Task/sdk";
import { useDealTeamMembers } from "entities/Team/sdk";

import { EChecklistSubmissionDialogStep } from "./constants";
import SubmitChecklistDialog from "./SubmitChecklistDialog";
import SubmittedChecklistDownloadDialog from "./SubmittedChecklistDownloadDialog";

const ANCHORS = [MAIN_NAVIGATION_ID, SUB_NAVIGATION_ID];

const DueDiligence = () => {
  const params = useParams<{
    dealId: string;
    packageId: string;
    tabId: string;
    categoryId?: string;
    sectionId?: string;
    fieldId?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
  const [submissionChecklistDialogStep, setSubmissionChecklistDialogStep] =
    useState<EChecklistSubmissionDialogStep>(
      (searchParams.get("submissionStep") as EChecklistSubmissionDialogStep) ||
        EChecklistSubmissionDialogStep.CLOSED
    );

  const dealId = Number(params.dealId);
  const packageId = Number(params.packageId);
  const tabId = Number(params.tabId);

  const queryParams = Object.assign(
    {},
    params.categoryId ? { category_id: params.categoryId } : null,
    params.sectionId ? { section_id: params.sectionId } : null,
    params.fieldId ? { field_id: params.fieldId } : null,
    params.tabId ? { tab_id: params.tabId } : null
  );

  const { data: companySettings } = useCompanySettings();
  const { data: deal } = useDealDetails({ dealId });
  const { loggedUserIsDealAdmin } = useLoggedUserIsDealAdmin({ dealId });
  const { data: userPermission, isValidating: isFetchingDealPermission } =
    useDealChecklistPermission({
      dealId,
      packageId,
    });
  const hasWriteAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.has_write_access,
    [userPermission]
  );

  const hasSubmissionPermission = useMemo(
    () =>
      !_.isNil(userPermission) &&
      userPermission.has_write_access &&
      !userPermission.is_checklist_owner,
    [userPermission]
  );

  const {
    data: checklist,
    isValidating: isFetchingChecklist,
    mutate: mutateChecklist,
    error: checklistError,
  } = usePackageDealDetails({
    packageId,
    dealId,
    queryParams,
  });
  const { mutate: mutateStatusCounts } = useCompanyPackageStatusCounts({
    dealId,
    packageId,
    enabled: hasWriteAccess,
  });

  const { mutate: refetchOnboardingCompanyPackageCategorizedFileFieldCounts } =
    useOnboardingCompanyPackageCategorizedFileFieldCounts({
      dealId,
      companyPackageId: packageId,
      enabled:
        (checklist?.is_onboarding &&
          companySettings?.onboarding_company_package_enabled) ||
        false,
    });
  const { isValidating: isFetchingDealTeamMembers } = useDealTeamMembers({
    dealId,
  });
  const { data: taskChannels, isValidating: isFetchingTaskChannels } =
    useTaskChannels({ dealId });
  const { isValidating: isFetchingSignedUser } = useSignedUser();
  const { mutate: mutateChecklistWithTabs, isValidating: isFetchingTabs } =
    useCompanyPackageWithTabsInformation({
      dealId,
      packageId,
    });

  const { mutate: mutateSnapshots } = useCompanyPackageSnapshotList({
    dealId,
    companyPackageId: packageId,
  });
  const { isValidating: isFetchingTags } = useTagList();

  const onSnapshotsUpdate = useCallback(
    () => mutateSnapshots(),
    [mutateSnapshots]
  );

  const navigate = useNavigate();

  useRedirectUponFetchFailure({
    error: checklistError,
    loading: isFetchingChecklist,
  });

  const [isExpanded, setIsExpanded] = useState(true);
  const {
    showOnlyUpdatedFields,
    showOnlyFileFields,
    showApprovalBoxes,
    toggleShowApprovalBoxes,
    statusFilter,
    showOnlyFieldsWithRemarks,
    showOnlyFieldsWithTasks,
    showOnlyFieldsWithUnreadMessages,
  } = useDueDiligenceFilters({
    selectedTab: tabId
      ? _.find(checklist?.tabs, ["tab_id", tabId])
      : _.first(checklist?.tabs),
  });
  const [navigationTab, setNavigationTab] = useState<DueDiligenceTabChoices>(
    DueDiligenceTabChoices.CHECKLIST_ITEMS
  );

  const selectedTab = useMemo(() => {
    const tab = tabId
      ? _.find(checklist?.tabs, ["tab_id", tabId])
      : _.first(checklist?.tabs);

    // Category -> section -> field

    if (!_.isNil(tab)) {
      let result = tab;

      if (showOnlyUpdatedFields) {
        // Drops all the categories -> sections -> fields that have their has_new set to false.
        result = {
          ...result,
          categories: result.categories
            .map((category) => {
              const filteredSections = category.sections
                .map((section) => {
                  const filteredFields = section.fields.filter(
                    (field) => field.has_new
                  );

                  if (filteredFields.length > 0) {
                    return {
                      ...section,
                      fields: filteredFields,
                    };
                  }
                  return null;
                })
                .filter((section) => section !== null);

              if (filteredSections.length > 0) {
                return {
                  ...category,
                  sections: filteredSections,
                };
              }
              return null;
            })
            .filter((category) => category !== null),
        };
      }

      if (showOnlyFileFields) {
        // Drops all the categories -> sections -> fields that are not file fields.
        result = {
          ...result,
          categories: result.categories
            .map((category) => {
              const filteredSections = category.sections
                .map((section) => {
                  const filteredFields = section.fields.filter(
                    (field) => field.field_type === FieldType.FILE
                  );

                  if (filteredFields.length > 0) {
                    return {
                      ...section,
                      fields: filteredFields,
                    };
                  }
                  return null;
                })
                .filter((section) => section !== null);

              if (filteredSections.length > 0) {
                return {
                  ...category,
                  sections: filteredSections,
                };
              }
              return null;
            })
            .filter((category) => category !== null),
        };
      }

      if (showOnlyFieldsWithTasks) {
        // Drops all the categories -> sections -> fields that do not have tasks.
        result = {
          ...result,
          categories: result.categories
            .map((category) => {
              const filteredSections = category.sections
                .map((section) => {
                  const filteredFields = section.fields.filter((field) => {
                    const tasksForField = taskChannels?.filter(
                      (taskChannel) =>
                        taskChannel.task.company_category?.id ===
                          category.category_id &&
                        taskChannel.task.company_section?.id ===
                          section.section_id &&
                        taskChannel.task.company_field?.id === field.field_id &&
                        !taskChannel.task.completed
                    );

                    return (tasksForField?.length || 0) > 0;
                  });

                  if (filteredFields.length > 0) {
                    return {
                      ...section,
                      fields: filteredFields,
                    };
                  }
                  return null;
                })
                .filter((section) => section !== null);

              if (filteredSections.length > 0) {
                return {
                  ...category,
                  sections: filteredSections,
                };
              }
              return null;
            })
            .filter((category) => category !== null),
        };
      }

      if (showOnlyFieldsWithUnreadMessages) {
        // Drops all the categories -> sections -> fields that do not have tasks.
        result = {
          ...result,
          categories: result.categories
            .map((category) => {
              const filteredSections = category.sections
                .map((section) => {
                  const filteredFields = section.fields.filter((field) => {
                    const tasksForField = taskChannels?.filter(
                      (taskChannel) =>
                        taskChannel.task.company_category?.id ===
                          category.category_id &&
                        taskChannel.task.company_section?.id ===
                          section.section_id &&
                        taskChannel.task.company_field?.id === field.field_id &&
                        !taskChannel.task.completed
                    );

                    return tasksForField?.some(
                      (taskChannel) => taskChannel.unread_count > 0
                    );
                  });

                  if (filteredFields.length > 0) {
                    return {
                      ...section,
                      fields: filteredFields,
                    };
                  }
                  return null;
                })
                .filter((section) => section !== null);

              if (filteredSections.length > 0) {
                return {
                  ...category,
                  sections: filteredSections,
                };
              }
              return null;
            })
            .filter((category) => category !== null),
        };
      }

      if (showOnlyFieldsWithRemarks) {
        // Drops all the categories -> sections -> fields that do not have remarks.
        result = {
          ...result,
          categories: result.categories
            .map((category) => {
              const filteredSections = category.sections
                .map((section) => {
                  const filteredFields = section.fields.filter((field) => {
                    const sharedRemark = _.first(field.shared_remarks);
                    const formattedSharedRemark = EMPTY_VALUE_REGEX.test(
                      sharedRemark?.shared_remark || ""
                    )
                      ? ""
                      : sharedRemark?.shared_remark;
                    const privateRemark = _.first(field.private_remarks);
                    const formattedPrivateRemark = EMPTY_VALUE_REGEX.test(
                      privateRemark?.private_remark || ""
                    )
                      ? ""
                      : privateRemark?.private_remark;

                    return (
                      !_.isEmpty(formattedSharedRemark) ||
                      !_.isEmpty(formattedPrivateRemark)
                    );
                  });

                  if (filteredFields.length > 0) {
                    return {
                      ...section,
                      fields: filteredFields,
                    };
                  }
                  return null;
                })
                .filter((section) => section !== null);

              if (filteredSections.length > 0) {
                return {
                  ...category,
                  sections: filteredSections,
                };
              }
              return null;
            })
            .filter((category) => category !== null),
        };
      }

      if (!_.isEmpty(statusFilter)) {
        result = {
          ...result,
          categories: result.categories
            .map((category) => {
              const filteredSections = category.sections
                .map((section) => {
                  const filteredFields = section.fields.filter((field) =>
                    statusFilter.includes(field.status)
                  );

                  if (filteredFields.length > 0) {
                    return {
                      ...section,
                      fields: filteredFields,
                    };
                  }
                  return null;
                })
                .filter((section) => section !== null);

              if (filteredSections.length > 0) {
                return {
                  ...category,
                  sections: filteredSections,
                };
              }
              return null;
            })
            .filter((category) => category !== null),
        };
      }

      return result;
    }

    return tab;
  }, [
    checklist,
    tabId,
    statusFilter,
    showOnlyUpdatedFields,
    showOnlyFileFields,
    showOnlyFieldsWithTasks,
    showOnlyFieldsWithRemarks,
    showOnlyFieldsWithUnreadMessages,
    taskChannels,
  ]);

  const tasksForSelectedTab = useMemo(
    () =>
      taskChannels?.filter(
        (taskChannel) =>
          taskChannel.task.company_tab?.id === selectedTab?.tab_id &&
          taskChannel.task.company_category === null &&
          taskChannel.task.company_section === null &&
          taskChannel.task.company_field === null &&
          !taskChannel.task.completed
      ) || [],
    [taskChannels, selectedTab?.tab_id]
  );

  useEffect(() => {
    if (!_.isNil(searchParams) && searchParams.has("tab")) {
      if (searchParams.get("tab") === DueDiligenceTabChoices.SAVED_SNAPSHOTS) {
        setNavigationTab(searchParams.get("tab") as DueDiligenceTabChoices);
      }

      if (loggedUserIsDealAdmin) {
        if (searchParams.get("tab") === DueDiligenceTabChoices.SHARE_SETTINGS) {
          setNavigationTab(searchParams.get("tab") as DueDiligenceTabChoices);
        } else if (
          searchParams.get("tab") === DueDiligenceTabChoices.SAVED_SNAPSHOTS
        ) {
          setNavigationTab(searchParams.get("tab") as DueDiligenceTabChoices);
        }
      }
    }
  }, [searchParams, loggedUserIsDealAdmin]);

  const selectedCategoryId = useMemo(() => {
    const categoryId = searchParams.get("categoryId");
    return !_.isNil(categoryId) ? Number(categoryId) : null;
  }, [searchParams]);

  useLayoutEffect(() => {
    // If we have the same categories in this tab, we will scroll to the first of them
    if (!_.isNil(selectedCategoryId)) {
      requestAnimationFrame(() => {
        scrollToSelectedAnchor({
          anchor: selectedCategoryId.toString(),
          navigationIds: ANCHORS,
        });
      });
    }
  }, [selectedCategoryId, selectedTab]);

  const handleTabChange = useCallback(
    (_: any, tabIndex: DueDiligenceTabChoices) => {
      setNavigationTab(tabIndex);
      const urlParameters: URLSearchParams = new URLSearchParams();
      urlParameters.set("tab", tabIndex);
      setSearchParams(urlParameters);
    },
    [setSearchParams]
  );

  usePageTitle(
    deal?.name && checklist?.name
      ? `Builders Patch: ${deal.name} - ${checklist?.name} - Checklist`
      : "Builders Patch: Checklists"
  );

  const loading = useMemo(
    () =>
      isFetchingChecklist ||
      isFetchingDealPermission ||
      isFetchingDealTeamMembers ||
      isFetchingTaskChannels ||
      isFetchingSignedUser ||
      isFetchingTabs ||
      isFetchingTags,
    [
      isFetchingChecklist,
      isFetchingDealPermission,
      isFetchingDealTeamMembers,
      isFetchingTaskChannels,
      isFetchingSignedUser,
      isFetchingTabs,
      isFetchingTags,
    ]
  );

  const handlePackageUpdate = useCallback(
    ({ message }: { message?: string }) => {
      mutateStatusCounts();
      mutateChecklist().then(() => {
        if (message) {
          toast.successMessage(message);
        }
      });
    },
    [mutateChecklist, mutateStatusCounts]
  );

  const handleTabMarkAllAsSeen = useCallback(() => {
    const fieldIds = _.flatMap(selectedTab?.categories, (category) =>
      _.flatMap(category?.sections, (section) =>
        _.flatMap(section?.fields, "field_id")
      )
    );

    fieldMarkAsSeenBulk({
      dealId,
      packageId,
      fieldIds,
    }).then(() => {
      mutateChecklistWithTabs();
      handlePackageUpdate?.({
        message: `Updated badges for ${selectedTab?.name} have been removed successfully!`,
      });
    });
  }, [
    dealId,
    selectedTab,
    handlePackageUpdate,
    packageId,
    mutateChecklistWithTabs,
  ]);

  const showOnboardingPackageSection = useMemo(
    () =>
      companySettings?.onboarding_company_package_shown &&
      checklist?.is_onboarding,
    [companySettings, checklist]
  );

  const [bulkUploadButton, categorizeDocumentsButton] = AIOnboardingButtons({
    handlePackageUpdate,
  });

  useCompanySectionAutomaticFillProcessing({
    successEventHandler: () => {
      mutateChecklist();
      refetchOnboardingCompanyPackageCategorizedFileFieldCounts();
    },
    failureEventHandler: () => mutateChecklist(),
  });

  useCompanyPackageSnapshotProcessing({
    successEventHandler: onSnapshotsUpdate,
    failureEventHandler: onSnapshotsUpdate,
  });

  useZipProcessing({
    successEventHandler: onSnapshotsUpdate,
    failureEventHandler: onSnapshotsUpdate,
  });

  const checklistProgressData = useChecklistProgress();

  const checklistLevelNotificationCount = useNotificationCountForChecklist();

  const fieldIds = useMemo(
    () =>
      _.flatMap(selectedTab?.categories, (category) =>
        _.flatMap(category?.sections, (section) =>
          _.flatMap(section?.fields, "field_id")
        )
      ),
    [selectedTab]
  );

  const onChecklistSubmissionStepOpen = useCallback(
    () =>
      setSubmissionChecklistDialogStep(
        EChecklistSubmissionDialogStep.SUBMISSION
      ),
    []
  );

  const onChecklistSubmisionDialogClose = useCallback(() => {
    setSubmissionChecklistDialogStep(EChecklistSubmissionDialogStep.CLOSED);
  }, []);

  const onChecklistDownloadPDForZipClose = useCallback(() => {
    setSubmissionChecklistDialogStep(EChecklistSubmissionDialogStep.CLOSED);

    searchParams.delete("submissionStep");
    searchParams.delete("snapshotId");
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const onChecklistSubmit = useCallback(() => {
    navigate(
      addQueryParams(
        reverse(URLS.PRINTABLE_CHECKLIST, {
          dealId,
          packageId,
        }),
        {
          submissionStep: EChecklistSubmissionDialogStep.DOWNLOAD,
          tab: navigationTab,
        }
      )
    );

    onChecklistSubmisionDialogClose();
  }, [
    onChecklistSubmisionDialogClose,
    dealId,
    packageId,
    navigationTab,
    navigate,
  ]);

  const isChecklistSubmittable = useMemo(
    () => (_.isNil(checklist) ? false : checklist.include_submission_button),
    [checklist]
  );

  const showDeleteAndRenameButtonsInSavedSnapshots = useMemo(
    () =>
      !_.isNil(userPermission) &&
      userPermission.is_full_access &&
      (loggedUserIsDealAdmin || userPermission.is_checklist_owner),
    [userPermission, loggedUserIsDealAdmin]
  );

  return (
    <Stack padding={theme.spacing(0, 4, 1, 4)}>
      <Box marginBottom={3}>
        <PageHeader
          title={checklist?.name || <Skeleton width={200} />}
          subTitle={
            <Stack gap={1} flexGrow={1}>
              <Text variant="text3" sx={{ color: colors.gray80 }}>
                {checklist?.company?.name || "Builders Patch"}
              </Text>

              {hasWriteAccess && (
                <ProgressBar
                  label="statuses"
                  sx={{ maxWidth: "530px" }}
                  progressData={checklistProgressData}
                />
              )}
            </Stack>
          }
          actions={
            <Stack gap={3} alignItems="flex-end">
              <Stack flexDirection="row" alignItems="center" gap={2}>
                {bulkUploadButton}

                <ActionButton
                  isOwned={checklist?.is_owned || false}
                  isSubmittable={isChecklistSubmittable}
                  dealId={dealId}
                  companyPackageId={packageId}
                  selectedTab={selectedTab}
                  navigationTab={navigationTab}
                  onChecklistSubmitDialogOpen={onChecklistSubmissionStepOpen}
                />
              </Stack>
              <Notifications {...checklistLevelNotificationCount} />
            </Stack>
          }
        />
      </Box>

      <DueDiligenceTabs
        dealId={dealId}
        packageId={packageId}
        selectedTab={navigationTab}
        onChange={handleTabChange}
      />

      {navigationTab === DueDiligenceTabChoices.CHECKLIST_ITEMS && (
        <>
          {(showOnboardingPackageSection ||
            categorizeDocumentsButton !== false) && (
            <Stack spacing={2} marginBottom={3}>
              {categorizeDocumentsButton}
              {showOnboardingPackageSection && (
                <OnboardingChecklistFieldCategories />
              )}
            </Stack>
          )}

          <Stack direction="row" gap={6} alignItems="flex-end" marginBottom={3}>
            <ItemSearch />

            <Stack gap={1}>
              <Text variant="labelSmall">display settings</Text>

              <Stack direction="row" gap={4}>
                <Tooltip title="Open or collapse all sections">
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={() => setIsExpanded((prevValue) => !prevValue)}
                    sx={{
                      cursor: "pointer",
                      transition: "opacity 0.3s linear",
                      opacity: isExpanded ? 1 : 0.5,
                      "&:hover": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Text variant="text3" noWrap fontWeight={500}>
                      All drawers opened
                    </Text>
                    <Switch size="small" checked={isExpanded} />
                  </Stack>
                </Tooltip>

                {hasWriteAccess && (
                  <Tooltip title="Show or hide status boxes">
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={toggleShowApprovalBoxes}
                      sx={{
                        cursor: "pointer",
                        transition: "opacity 0.3s linear",
                        opacity: showApprovalBoxes ? 1 : 0.5,
                        "&:hover": {
                          opacity: 1,
                        },
                      }}
                    >
                      <Text variant="text3" noWrap fontWeight={500}>
                        Status displayed
                      </Text>
                      <Switch checked={showApprovalBoxes} size="small" />
                    </Stack>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </Stack>
        </>
      )}

      <Paper
        square={true}
        sx={{
          padding: 0,
          flex: 1,
          position: "relative",
        }}
        data-testid="checklist-page-content"
      >
        {loading && (
          <LinearProgress
            sx={{ position: "absolute", width: "100%", zIndex: 2 }}
          />
        )}
        <Stack
          spacing={2}
          direction="row"
          alignItems="flex-start"
          data-testid="dd-left-side-menu"
        >
          {navigationTab === DueDiligenceTabChoices.CHECKLIST_ITEMS && (
            <LeftMenu />
          )}
          <Paper style={{ flex: 1, overflowX: "auto", padding: 0 }}>
            {_.isNil(checklist) ? (
              <>
                <Box sx={{ marginBottom: theme.spacing(2) }}>
                  <Skeleton variant="text" width={200} height={54} />
                  <Skeleton variant="text" width={200} />
                </Box>
                <CategorySkeleton />
              </>
            ) : (
              <>
                {navigationTab === DueDiligenceTabChoices.CHECKLIST_ITEMS && (
                  <>
                    <Stack
                      sx={{
                        backgroundColor: colors.blue10,
                        marginBottom: 1,
                        paddingY: 1,
                        paddingX: 2,
                        borderRadius: "8px",
                      }}
                      data-testid="checklist-tab-header"
                    >
                      <Stack
                        direction="row"
                        gap={2}
                        justifyContent="space-between"
                      >
                        <Stack gap={0.5}>
                          <Text variant="labelSmall">current tab</Text>
                          <Text variant="h3">{selectedTab?.name}</Text>
                        </Stack>

                        <Stack direction="row" gap={1}>
                          {!_.isNil(selectedTab) &&
                            !_.isEmpty(tasksForSelectedTab) && (
                              <DueDiligenceTabOpenTaskButton
                                tab={selectedTab}
                                packageId={packageId}
                                dealId={dealId}
                                updatePackage={handlePackageUpdate}
                                taskChannels={tasksForSelectedTab}
                              />
                            )}
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: "uppercase" }}
                            onClick={() => setIsHeaderExpanded((prev) => !prev)}
                            data-testid="filters-and-statuses-button"
                          >
                            filters & statuses{" "}
                            {isHeaderExpanded ? (
                              <KeyboardArrowUpOutlined />
                            ) : (
                              <KeyboardArrowDownOutlined />
                            )}
                          </Button>

                          <ButtonDropdown
                            variant="text"
                            label={<MoreVertIcon />}
                            width="auto"
                            size="small"
                            sx={{ padding: "2px" }}
                            keepMenuMounted
                          >
                            {_.isEmpty(selectedTab)
                              ? [
                                  <MenuItem>
                                    <Text variant="text2">
                                      <Skeleton width="132px" />
                                    </Text>
                                  </MenuItem>,
                                  <MenuItem>
                                    <Text variant="text2">
                                      <Skeleton width="132px" />
                                    </Text>
                                  </MenuItem>,
                                ]
                              : [
                                  hasWriteAccess && [
                                    <GroupLabel label="status: mark all as" />,
                                    <StatusSelector
                                      fieldIds={fieldIds}
                                      companyPackageId={packageId}
                                      dealId={dealId}
                                      successCallback={handlePackageUpdate}
                                    />,
                                  ],
                                  <GroupLabel label="actions" />,
                                  loggedUserIsDealAdmin && (
                                    <ShareWithButton
                                      initiallySelectedItemIds={selectedTab.categories.map(
                                        (category) => category.category_id
                                      )}
                                    />
                                  ),
                                  <MenuItem
                                    onClick={handleTabMarkAllAsSeen}
                                    dataTestid="clear-updated-tags-option"
                                  >
                                    <Text variant="text2">
                                      Clear updated tags
                                    </Text>
                                  </MenuItem>,
                                ]}
                          </ButtonDropdown>
                        </Stack>
                      </Stack>

                      <Collapse
                        in={isHeaderExpanded}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Stack marginTop={3}>
                          <FilteredViews
                            tab={
                              tabId
                                ? _.find(checklist?.tabs, ["tab_id", tabId])
                                : _.first(checklist?.tabs)
                            }
                          />
                        </Stack>
                      </Collapse>
                    </Stack>
                    {_.uniqBy(selectedTab?.categories, "category_id").map(
                      (category) => (
                        <Category
                          key={category.category_id}
                          isFocused={
                            category.category_id === selectedCategoryId
                          }
                          category={category}
                          isExpanded={isExpanded}
                          dataTestid="dd-category"
                          disableEdits={loading}
                          handlePackageUpdate={handlePackageUpdate}
                        />
                      )
                    )}
                    {_.isEmpty(selectedTab?.categories) && (
                      <Box
                        sx={{ paddingY: theme.spacing(5) }}
                        data-testid="no-dd-category-permission-view"
                      >
                        <Text
                          variant="h5"
                          textAlign="center"
                          data-testid="no-dd-permission-message"
                        >
                          No results.
                        </Text>
                      </Box>
                    )}
                    <Footer
                      selectedTab={selectedTab}
                      onChecklistSubmitDialogOpen={
                        onChecklistSubmissionStepOpen
                      }
                      isSubmittable={
                        isChecklistSubmittable && hasSubmissionPermission
                      }
                    />
                  </>
                )}
                {navigationTab === DueDiligenceTabChoices.ALL_FILES && (
                  <ZipDetails dealId={dealId} packageId={packageId} />
                )}
                {navigationTab === DueDiligenceTabChoices.SHARE_SETTINGS && (
                  <ShareSettings dealId={dealId} companyPackageId={packageId} />
                )}
                {navigationTab === DueDiligenceTabChoices.SAVED_SNAPSHOTS && (
                  <SavedSnapshots
                    showDeleteAndRenameButtons={
                      showDeleteAndRenameButtonsInSavedSnapshots
                    }
                  />
                )}
                {hasSubmissionPermission && (
                  <>
                    <SubmitChecklistDialog
                      open={submissionChecklistDialogStep}
                      checklistProgressData={checklistProgressData}
                      onSubmit={onChecklistSubmit}
                      onClose={onChecklistSubmisionDialogClose}
                    />
                    <SubmittedChecklistDownloadDialog
                      open={submissionChecklistDialogStep}
                      onClose={onChecklistDownloadPDForZipClose}
                    />
                  </>
                )}
              </>
            )}
          </Paper>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default DueDiligence;
